<!--
  CCTermsAcknowledgement
  ----------------------
  Popup shown after login when the consultant has a Director's agreement that has not
  been accepted yet. The BeeTerms row is created by the month-end close
  (GenActDirectorsAgreement); this component only displays it and records the acceptance.

  Blocking, like Prowess: it covers every consultant page until the agreement is accepted.
  The only other way out is logging out (Prowess: declining meant not logging in).
-->
<template>
  <div class="terms-ack-root">
    <!--
      Always rendered on purpose: mountTag (main.ts) only moves element children out of the mount
      tag before removing it, so a root that starts as v-if=false would stay in the removed tag.
    -->
    <div v-if="visible" class="terms-ack-overlay" role="dialog" aria-modal="true">
      <div class="terms-ack-modal">
        <div class="terms-ack-header">
          <h4>{{ currentAgreement?.termsDescription || t('termsAcknowledgement.title') }}</h4>
        </div>

        <div class="terms-ack-body">
          <!-- Terms bodies are authored as HTML in GenTermsVersion.Terms, same as the NUW contract step -->
          <div v-if="currentAgreement?.terms" class="terms-ack-contract">
            <div v-html="currentAgreement.terms" class="terms-ack-text"></div>
          </div>
          <p v-else class="terms-ack-no-document">
            {{ t('termsAcknowledgement.noDocument') }}
          </p>
        </div>

        <div class="terms-ack-footer">
          <p v-if="error" class="terms-ack-error">{{ error }}</p>

          <!-- Always required, like the Prowess cbAccept: the agreement text itself refers to ticking a box -->
          <div class="terms-ack-checkbox">
            <KCheckBox
              v-model="accepted"
              :disabled="saving"
              :label="currentAgreement?.shortTermsAckText || t('termsAcknowledgement.agreementCheckbox')"
            ></KCheckBox>
          </div>

          <div class="terms-ack-actions">
            <AoeLogoutButton />
            <button
              type="button"
              class="terms-ack-primary"
              :disabled="saving || !accepted"
              @click="acceptCurrent"
            >
              {{ saving ? t('termsAcknowledgement.saving') : t('termsAcknowledgement.submit') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLogger } from 'vue-logger-plugin'
import { PwsProfileTermsGetList, PwsProfileTermsAccept } from '../../api/ConsultantCorner'
import { GetBeeNumberAuthenicatedUser } from '../../stores/SettingsStore'
import type { IContract } from '../../types/ConsultantCornerTypes'
import AoeLogoutButton from '../AoeLogoutButton.vue'
import { Checkbox as KCheckBox } from '@progress/kendo-vue-inputs'

const DIRECTOR_AGREEMENT_TERMS_TYPE = 'DIRECTORAGREEMENT'

const { t } = useI18n()
const logger = useLogger()

const pending = ref<IContract[]>([])
const visible = ref(false)
const accepted = ref(false)
const saving = ref(false)
const error = ref('')

const currentAgreement = computed<IContract | null>(() => pending.value[0] ?? null)

function isAccepted(contract: IContract): boolean {
  return !!contract.acknowledgeDate || contract.codeIdAckAction?.codeId?.toUpperCase() === 'ACCEPT'
}

// Directors sign again on every promotion, so a consultant can have accepted rows from earlier
// promotions next to the pending one: any row not accepted yet shows the popup. Rows are grouped
// by agreement because accepting it signs all of its pending rows at once.
// Declined rows are still pending, so the consultant is asked again.
function getPendingAgreements(items: IContract[]): IContract[] {
  const pendingByTermsId = new Map<string, IContract>()
  items
    .filter(item => item.codeIdTermsType?.codeId?.toUpperCase() === DIRECTOR_AGREEMENT_TERMS_TYPE)
    .filter(item => !isAccepted(item))
    .forEach(item => {
      const existing = pendingByTermsId.get(item.termsId)
      if (!existing || item.versionNumber > existing.versionNumber) {
        pendingByTermsId.set(item.termsId, item)
      }
    })

  return [...pendingByTermsId.values()]
}

const loadPendingAgreements = async () => {
  try {
    const beeNumber = await GetBeeNumberAuthenicatedUser()
    if (!beeNumber) {
      logger.debug('Terms acknowledgement: no authenticated bee number, skipping')
      return
    }

    const response = await PwsProfileTermsGetList({ beeNumber })
    pending.value = getPendingAgreements(response?.items || [])
    visible.value = pending.value.length > 0
  } catch (err) {
    // Never block the page on this - log and stay hidden.
    logger.error('Error loading pending terms for acknowledgement popup:', err)
  }
}

const acceptCurrent = async () => {
  const agreement = currentAgreement.value
  if (!agreement) return

  try {
    saving.value = true
    error.value = ''
    await PwsProfileTermsAccept({ termsId: agreement.termsId })

    pending.value = pending.value.slice(1)
    accepted.value = false
    visible.value = pending.value.length > 0
  } catch (err) {
    logger.error('Error accepting terms:', err)
    error.value = t('termsAcknowledgement.errorAccepting')
  } finally {
    saving.value = false
  }
}

onMounted(() => {
  loadPendingAgreements()
})
</script>

<style lang="scss" scoped>
.terms-ack-overlay {
  position: fixed;
  inset: 0;
  background: rgba(black, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: $spacing-4;
}

.terms-ack-modal {
  background: white;
  border-radius: $radius-lg;
  box-shadow: 0 4px 24px rgba(black, 0.3);
  width: 100%;
  max-width: 720px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.terms-ack-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $gap-md;
  padding: $card-padding;
  border-bottom: 1px solid #e9ecef;
}

.terms-ack-header h4 {
  @include typography-body-large;
  @include font-weight-semibold;
  margin: 0;
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.terms-ack-body {
  padding: $card-padding;
  overflow-y: auto;
}

// Same look as the contract in the NUW ContractAgreementStep
.terms-ack-contract {
  max-height: 50vh;
  overflow-y: auto;
  padding: $spacing-4;
  border: 1px solid #e0e0e0;
  border-radius: $radius-sm;
  background-color: var(--vt-c-white-soft);
}

// Unlike the NUW step, normal white-space: the Director's agreement is HTML with spacing between tags
.terms-ack-text {
  @include typography-small;
  line-height: 1.5;
}

.terms-ack-no-document {
  @include typography-body;
  margin: $spacing-4 0;
  color: #666;
}

.terms-ack-footer {
  padding: $card-padding;
  border-top: 1px solid #e9ecef;
}

.terms-ack-error {
  @include typography-small;
  color: #e53935;
  margin: 0 0 $spacing-3 0;
}

.terms-ack-checkbox {
  @include typography-small;
  margin-bottom: $spacing-4;
}

.terms-ack-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: $gap-md;
}

.terms-ack-primary {
  @include typography-small;
  padding: $spacing-2 $spacing-4;
  border-radius: $button-radius-lg;
  cursor: pointer;
}

.terms-ack-primary {
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  color: white;
  border: none;
}

.terms-ack-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 767px) {
  .terms-ack-modal {
    max-height: 95vh;
  }

  .terms-ack-actions {
    flex-direction: column-reverse;
  }

  .terms-ack-primary {
    width: 100%;
  }
}
</style>
